import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from './firebase';
import { doc, getDoc, onSnapshot, updateDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { cacheHelpers } from './utils/campaignCache';

const UserContext = createContext();

const SELECTED_COMPANY_KEY = 'selectedCompanyId';

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [hasActiveTrial, setHasActiveTrial] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionType, setSubscriptionType] = useState(null);
  const [trialEndDate, setTrialEndDate] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [agencyId, setAgencyId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      // Only set loading true if this is the initial auth check
      if (user === null && userProfile === null) {
        setLoading(true);
      }

      if (currentUser) {
        setUser(currentUser);
        if (!userProfile) {
          await loadUserProfile(currentUser.uid);
        }
      } else {
        resetUserState();
        setLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  // Add effect to check localStorage on initial load
  useEffect(() => {
    const loadSelectedCompany = async () => {
      const selectedCompanyId = localStorage.getItem(SELECTED_COMPANY_KEY);
      if (selectedCompanyId && user) {
        // Only load if we have both a stored ID and a logged-in user
        const companyDoc = await getDoc(doc(db, 'company', selectedCompanyId));
        if (companyDoc.exists()) {
          setBusinessProfile({ id: companyDoc.id, ...companyDoc.data() });
        }
      }
    };

    loadSelectedCompany();
  }, [user]); // Depend on user to ensure we have authentication

  useEffect(() => {
    if (user?.companyId) {
      setAgencyId(user.companyId);
    } else if (businessProfile?.id && !businessProfile.parentCompany) {
      // If businessProfile is loaded and it's not a client (no parentCompany)
      setAgencyId(businessProfile.id);
    }
  }, [user, businessProfile]);

  const resetUserState = () => {
    setUser(null);
    setUserProfile(null);
    setBusinessProfile(null);
    setIsOwner(false);
    setHasActiveTrial(false);
    setSubscriptionStatus(null);
    setSubscriptionType(null);
    setTrialEndDate(null);
    setCampaigns([]);
    setAgencyId(null);
    localStorage.removeItem(SELECTED_COMPANY_KEY); // Clear selected company on logout
  };

  const loadUserProfile = async (userId) => {
    const fetchWithRetry = async (retries = 3) => {
      try {
        const userDocRef = doc(db, 'users', userId);
        const userSnapshot = await getDoc(userDocRef);
        
        if (userSnapshot.exists()) {
          const userData = userSnapshot.data();
          setUserProfile(userData);
          
          // Determine which company to use
          const selectedCompanyId = localStorage.getItem(SELECTED_COMPANY_KEY);
          const companyIdToUse = selectedCompanyId || userData.companyId;
          
          if (companyIdToUse) {
            const businessDocRef = doc(db, 'company', companyIdToUse);
            
            // OPTIMIZATION: Check cache first for business profile
            const cachedBusinessProfile = cacheHelpers.getCachedUserCampaigns(companyIdToUse);
            
            const unsubscribeBusiness = onSnapshot(businessDocRef, async (snapshot) => {
              if (snapshot.exists()) {
                const businessData = { id: snapshot.id, ...snapshot.data() };
                setBusinessProfile(businessData);
                setIsOwner(businessData.ownerId === userId);
                
                // Find the parent agency for subscription checks
                const agencyId = businessData.parentCompany || businessData.id;
                const agencyDocRef = doc(db, 'company', agencyId);
                const agencySnapshot = await getDoc(agencyDocRef);
                
                if (agencySnapshot.exists()) {
                  // Setup subscription listener using the agency owner's ID
                  setupSubscriptionListener(agencySnapshot.data().ownerId);
                }

                // OPTIMIZATION: Setup campaigns listener with caching
                setupOptimizedCampaignsListener(businessData.id);

                checkActiveTrial(businessData);
                setLoading(false);

                return () => {
                  // Cleanup will be handled by the listener
                };
              } else {
                retryOrError();
              }
            });
          } else {
            retryOrError();
          }
        } else {
          retryOrError();
        }
      } catch (error) {
        console.error(`Error loading user profile (attempt ${4 - retries}):`, error);
        if (retries > 1) {
          setTimeout(() => fetchWithRetry(retries - 1), 1000);
        } else {
          setLoading(false);
        }
      }
    };

    const retryOrError = () => {
      setLoading(false);
    };

    await fetchWithRetry();
  };

  // OPTIMIZATION: Enhanced campaigns listener with caching
  const setupOptimizedCampaignsListener = (companyId) => {
    // Check cache first
    const cachedCampaigns = cacheHelpers.getCachedUserCampaigns(companyId);
    if (cachedCampaigns) {
      setCampaigns(cachedCampaigns);
    }

    const campaignsRef = collection(db, 'campaign');
    const campaignsQuery = query(
      campaignsRef, 
      where("companyId", "==", companyId),
      orderBy("createdAt", "desc")
    );
    
    return onSnapshot(campaignsQuery, (snapshot) => {
      const campaignsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Update cache
      cacheHelpers.cacheUserCampaigns(companyId, campaignsData);
      
      setCampaigns(campaignsData);
    });
  };

  const setupSubscriptionListener = (ownerId) => {
    if (!ownerId) return;



    // Use the correct path: users/{ownerId}/subscriptions collection
    const subscriptionsRef = collection(db, 'users', ownerId, 'subscriptions');
    
    const unsubscribeSubscription = onSnapshot(subscriptionsRef, (snapshot) => {
      
      if (!snapshot.empty) {
        const subscriptions = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Find the most recent active subscription
        const activeSubscription = subscriptions.find(sub => 
          sub.status === 'active' || sub.status === 'trialing' || sub.status === 'payment_overdue'
        );
        
        if (activeSubscription) {
          setSubscriptionStatus(activeSubscription.status);
          
          // Extract subscription type from the product metadata or name
          const productName = activeSubscription.items?.[0]?.price?.product?.name || '';
          const userType = activeSubscription.items?.[0]?.price?.product?.metadata?.userType || '';
          
          if (userType === 'standard' || productName.toLowerCase().includes('standard')) {
            setSubscriptionType('standard');
          } else if (userType === 'agency' || productName.toLowerCase().includes('agency')) {
            setSubscriptionType('agency');
          }
          
          // Set trial end date if trialing (using Firebase timestamp)
          if (activeSubscription.status === 'trialing' && activeSubscription.trial_end) {
            const trialEndDate = new Date(activeSubscription.trial_end.seconds * 1000);
            setTrialEndDate(trialEndDate);
          }
        } else {
          setSubscriptionStatus(null);
          setSubscriptionType(null);
          setTrialEndDate(null);
        }
      } else {
        setSubscriptionStatus(null);
        setSubscriptionType(null);
        setTrialEndDate(null);
      }
    });

    return unsubscribeSubscription;
  };

  const checkActiveTrial = (businessData) => {
    if (businessData.trialStartDate && businessData.trialEndDate) {
      const now = new Date();
      const trialEnd = new Date(businessData.trialEndDate);
      setHasActiveTrial(now <= trialEnd);
      if (now <= trialEnd) {
        setTrialEndDate(trialEnd);
      }
    } else {
      setHasActiveTrial(false);
    }
  };

  const updateUserProfile = async (updates) => {
    if (!user) return;
    
    try {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, updates);
      setUserProfile(prev => ({ ...prev, ...updates }));
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  };

  const updateCompanyProfile = async (updates) => {
    if (!businessProfile) return;
    
    try {
      const companyDocRef = doc(db, 'company', businessProfile.id);
      await updateDoc(companyDocRef, updates);
      setBusinessProfile(prev => ({ ...prev, ...updates }));
    } catch (error) {
      console.error('Error updating company profile:', error);
      throw error;
    }
  };

  const switchActiveClient = async (clientCompany) => {
    try {
      if (!clientCompany) {
        // If no client provided, revert to the user's original company
        localStorage.removeItem(SELECTED_COMPANY_KEY); // Clear selected company
        
        if (!userProfile?.companyId) {
          console.error('No original company ID available in user profile');
          return;
        }
  
        const agencyDoc = await getDoc(doc(db, 'company', userProfile.companyId));
        if (agencyDoc.exists()) {
          const businessData = { id: agencyDoc.id, ...agencyDoc.data() };
          setBusinessProfile(businessData);
          
          // Setup subscription listener using the agency owner's ID
          if (businessData.ownerId) {
            setupSubscriptionListener(businessData.ownerId);
          }
          
          // Reset campaigns for agency
          setupOptimizedCampaignsListener(agencyDoc.id);
        } else {
          setBusinessProfile(null);
          setCampaigns([]);
        }
      } else {
        // Switch to the client's profile
        setBusinessProfile({ id: clientCompany.id, ...clientCompany });
        localStorage.setItem(SELECTED_COMPANY_KEY, clientCompany.id);
        
        // Get the parent agency for subscription checks
        if (!clientCompany.parentCompany) {
          console.error('No parent company ID available for client');
          return;
        }
  
        const agencyDoc = await getDoc(doc(db, 'company', clientCompany.parentCompany));
        if (agencyDoc.exists()) {
          const agencyData = agencyDoc.data();
          if (agencyData.ownerId) {
            setupSubscriptionListener(agencyData.ownerId);
          } else {
            console.error('No owner ID found for agency');
          }
        } else {
          console.error('Agency document not found');
        }
        
        // Reset campaigns for new client
        setupOptimizedCampaignsListener(clientCompany.id);
      }
    } catch (error) {
      console.error('Error switching client:', error);
    }
  };

  const setupCampaignsListener = (companyId) => {
    const campaignsRef = collection(db, 'campaign');
    const campaignsQuery = query(
      campaignsRef, 
      where("companyId", "==", companyId),
      orderBy("createdAt", "desc")
    );
    
    return onSnapshot(campaignsQuery, (snapshot) => {
      const campaignsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCampaigns(campaignsData);
    });
  };

  return (
    <UserContext.Provider
      value={{
        user,
        userProfile,
        businessProfile,
        isOwner,
        hasActiveTrial,
        loading,
        updateUserProfile,
        updateCompanyProfile,
        subscriptionStatus,
        subscriptionType,
        trialEndDate,
        campaigns,
        agencyId,
        switchActiveClient,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

// Helper function for fetching user profile data
const fetchUserProfile = async (userId) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    if (userDoc.exists()) {
      return userDoc.data();
    }
    console.error('No such user document found for userId:', userId);
    return null;
  } catch (error) {
    console.error('Error fetching user profile for userId:', userId, error);
    return null;
  }
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
